import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, Loader2, RefreshCw, Lock, ArrowRight, Shuffle, Search } from 'lucide-react';
import {
  XbPage, WithScheme, SchemeHeading, Card, Fact, C, Notice, Copyable, errorText, money,
} from './common';
import xbApi from '../../services/xbApi';
import { label, countryName } from '../../utils/crossBorder';
import { uetr as newUetr } from '../../utils/xbSamples';
import { formatDate } from '../../utils/helpers';

const STATE_TONE = {
  QUOTED: 'bg-blue-50 text-blue-700 border-blue-200',
  LOCKED: 'bg-green-50 text-green-700 border-green-200',
  CONSUMED: 'bg-gray-100 text-gray-700 border-gray-200',
  EXPIRED: 'bg-amber-50 text-amber-800 border-amber-200',
  RELEASED: 'bg-amber-50 text-amber-800 border-amber-200',
  DECLINED: 'bg-red-50 text-red-700 border-red-200',
};
const STATE_WORDS = {
  QUOTED: 'Quoted: accept it to lock the rate',
  LOCKED: 'Locked for the payment with this UETR',
  CONSUMED: 'Used by its payment',
  EXPIRED: 'Expired: ask for a new one',
  RELEASED: 'Released: its payment did not go through',
  DECLINED: 'Declined',
};

const historyKey = (profileId) => `portal.xbQuotes.${profileId}`;
function readHistory(profileId) {
  try { return JSON.parse(sessionStorage.getItem(historyKey(profileId)) || '[]'); } catch { return []; }
}
function remember(profileId, quoteId) {
  try {
    const list = [quoteId, ...readHistory(profileId).filter((q) => q !== quoteId)].slice(0, 10);
    sessionStorage.setItem(historyKey(profileId), JSON.stringify(list));
  } catch { /* storage unavailable */ }
}

/**
 * FX quotes under a scheme whose rate locks: ask for a quote on a corridor,
 * accept it to lock the rate for one payment (bound by its UETR), then send
 * that payment. The rates are the Sandbox's teaching rates.
 */
export default function XbQuotes() {
  return (
    <XbPage icon={Coins} title="FX quotes"
      intro="Price a payment before you send it: a quote converts your amount at the scheme's rate, and accepting it locks the rate for that payment.">
      <WithScheme>{(scheme) => <QuotesBody key={scheme.profileId} scheme={scheme} />}</WithScheme>
    </XbPage>
  );
}

function QuotesBody({ scheme }) {
  const s = scheme.profile;
  const corridors = (s.corridors || []).filter((c) => c.from === scheme.homeCountry && c.send === scheme.homeCurrency);
  const bases = s.fx?.bases?.length ? s.fx.bases : ['FIXED_SEND'];
  const [to, setTo] = useState(corridors[0]?.to || '');
  const [basis, setBasis] = useState(bases[0]);
  const [amount, setAmount] = useState('100000');
  const [uetr, setUetr] = useState(newUetr);
  const [steer, setSteer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [history, setHistory] = useState(() => readHistory(scheme.profileId));
  const [lookup, setLookup] = useState('');

  if (!s.offers?.quotes) {
    return (
      <>
        <SchemeHeading summary={s} />
        <Notice tone="info" title="No quotes under this scheme">
          {label('fx', s.fx?.model)}.{' '}
          {s.fx?.model === 'SCHEME_CONVERTS'
            ? 'Its rates are indicative: the scheme applies its own rate when it processes the payment, so there is nothing to lock beforehand.'
            : 'The scheme does not convert, so it gives no quotes.'}
        </Notice>
      </>
    );
  }

  const corridor = corridors.find((c) => c.to === to);
  const amountCurrency = corridor ? (basis === 'FIXED_SEND' ? corridor.send : corridor.receive) : '';

  const run = async (fn) => {
    setBusy(true); setError(null);
    try {
      const q = await fn();
      setQuote(q);
      remember(scheme.profileId, q.quoteId);
      setHistory(readHistory(scheme.profileId));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const ask = () => run(() => xbApi.createQuote(scheme.profileId,
    { to, basis, amount: amount.trim(), uetr: uetr.trim() }, steer || undefined));
  const accept = () => run(() => xbApi.acceptQuote(scheme.profileId, quote.quoteId));
  const read = (id) => run(() => xbApi.quote(scheme.profileId, id));

  return (
    <>
      <SchemeHeading summary={s} />
      <Notice tone="info" title="How a quote is used">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Ask for a quote on a corridor, fixing what the sender pays or what the receiver gets, with the UETR
            your payment will carry.</li>
          <li>Accept it within {s.fx.quoteValidSeconds || 60} s: the rate is locked for that UETR.</li>
          <li>Send the pacs.008 with that UETR and the quote's amounts: <C>IntrBkSttlmAmt</C> is the send amount;
            for a fixed receive, <C>InstdAmt</C> is the receive amount and <C>XchgRate</C> the quote's message rate.
            The switch uses the quote up; a payment that does not match it is refused and the quote stays usable.</li>
        </ol>
      </Notice>

      <Card title="Ask for a quote" icon={Coins}>
        {corridors.length === 0 ? (
          <p className="text-sm text-amber-800">No corridor of this scheme leaves {scheme.homeCountry} in {scheme.homeCurrency}.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block text-xs font-medium text-gray-700">
                To
                <select value={to} onChange={(e) => setTo(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  {corridors.map((c) => <option key={c.to} value={c.to}>{countryName(c.to)} (paid out in {c.receive})</option>)}
                </select>
              </label>
              <fieldset className="text-xs font-medium text-gray-700">
                <legend>Fix</legend>
                <div className="mt-1 flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                  {bases.map((b) => (
                    <button key={b} type="button" onClick={() => setBasis(b)} aria-pressed={basis === b}
                      className={`flex-1 px-3 py-2 ${basis === b ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                      {b === 'FIXED_SEND' ? 'What I send' : 'What they get'}
                    </button>
                  ))}
                </div>
              </fieldset>
              <label className="block text-xs font-medium text-gray-700">
                Amount in {amountCurrency}
                <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                {corridor?.maxSend && <span className="block mt-1 font-normal text-gray-500">At most {corridor.maxSend} {corridor.send} sent.</span>}
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block text-xs font-medium text-gray-700 sm:col-span-2">
                UETR of the payment
                <span className="mt-1 flex gap-2">
                  <input value={uetr} onChange={(e) => setUetr(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                  <button type="button" onClick={() => setUetr(newUetr())} title="A new UETR" aria-label="A new UETR"
                    className="px-3 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                    <Shuffle className="w-4 h-4" aria-hidden="true" />
                  </button>
                </span>
              </label>
              <label className="block text-xs font-medium text-gray-700">
                Test the provider <span className="font-normal text-gray-500">(optional)</span>
                <select value={steer} onChange={(e) => setSteer(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Normal quote</option>
                  <option value="FX_DECLINE">The provider declines</option>
                  <option value="FX_EXPIRE:10">Expires in 10 s</option>
                </select>
              </label>
            </div>
            <button type="button" onClick={ask} disabled={busy || !to || !amount.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Coins className="w-4 h-4" aria-hidden="true" />}
              Get a quote
            </button>
          </div>
        )}
        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      </Card>

      {quote && <QuoteCard quote={quote} busy={busy} onAccept={accept} onRefresh={() => read(quote.quoteId)} />}

      <Card title="Look up a quote" icon={Search}>
        <div className="flex gap-2 flex-wrap">
          <input value={lookup} onChange={(e) => setLookup(e.target.value)} placeholder="Quote id"
            className="flex-1 min-w-[14rem] border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
          <button type="button" onClick={() => lookup.trim() && read(lookup.trim())} disabled={busy}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">Read</button>
        </div>
        {history.length > 0 && (
          <p className="text-xs text-gray-500 mt-3">Quotes you asked for in this browser session:{' '}
            {history.map((id) => (
              <button key={id} type="button" onClick={() => read(id)} className="font-mono underline mr-2">{id}</button>
            ))}
          </p>
        )}
      </Card>
    </>
  );
}

function QuoteCard({ quote, busy, onAccept, onRefresh }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (quote.state !== 'QUOTED' && quote.state !== 'LOCKED') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [quote.state]);
  const left = quote.expiresAt ? Math.max(0, Math.round((new Date(quote.expiresAt).getTime() - now) / 1000)) : null;

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" aria-live="polite">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Quote <span className="font-mono">{quote.quoteId}</span></h2>
          <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full border ${STATE_TONE[quote.state] || ''}`}>
            {STATE_WORDS[quote.state] || quote.state}</span>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onRefresh} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" aria-hidden="true" /> Refresh
          </button>
          {quote.state === 'QUOTED' && (
            <button type="button" onClick={onAccept} disabled={busy || left === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
              <Lock className="w-4 h-4" aria-hidden="true" /> Accept and lock
            </button>
          )}
          {quote.state === 'LOCKED' && (
            <button type="button" onClick={() => navigate('/xb/samples', { state: { quote } })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
              Send the payment <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <dl className="grid gap-x-8 md:grid-cols-2">
        <Fact label="The sender pays">{money(quote.send)}</Fact>
        <Fact label="The receiver gets">{money(quote.receive)}</Fact>
        <Fact label="Rate">{quote.rate}</Fact>
        <Fact label="Message rate (XchgRate)">{quote.xchgRate || '—'}</Fact>
        {quote.fee && <Fact label="Fee">{money(quote.fee)}</Fact>}
        <Fact label="Fixed">{quote.basis === 'FIXED_SEND' ? 'what the sender pays' : 'what the receiver gets'}</Fact>
        <Fact label="UETR"><Copyable value={quote.uetr} label="the UETR" /></Fact>
        <Fact label="Expires">{quote.expiresAt ? `${formatDate(quote.expiresAt, 'pp')}${left !== null && (quote.state === 'QUOTED' || quote.state === 'LOCKED') ? ` (in ${left} s)` : ''}` : '—'}</Fact>
        {quote.lockedAt && <Fact label="Locked">{formatDate(quote.lockedAt, 'pp')}</Fact>}
        {quote.consumedAt && <Fact label="Used">{formatDate(quote.consumedAt, 'pp')}</Fact>}
      </dl>
      {quote.note && <p className="text-sm text-gray-700 mt-3">{quote.note}</p>}
      <p className="text-xs text-gray-500 mt-3">{quote.notice}</p>
    </section>
  );
}
